import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import StandardInputText from '@/common/components/input/StandardInputText';
import { AddIcon, CancelIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import LocalStorageService from '@/services/storage/general/local-storage-service';
import { CourseData, SubunitBuilderData, SubunitData, UnitId } from 'course-types';
import LoadingSpinner from '../../LoadingSpinner';

const DefaultSubunit: SubunitBuilderData = {
  name: '',
  activities: [],
};

function SubunitCreatorWizard() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const modalName: string = useAppSelector(() => store.getState().modal.name);
  const unitId: UnitId = useAppSelector(() => store.getState().unit.id);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [unitContentCount, setUnitContentCount] = useState<number>(0);
  const [newSubunitData, setNewSubunitData] = useState<SubunitBuilderData>(DefaultSubunit);
  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setUnitContentCount(
      Object.entries(activeCourseData.units[unitId as UnitId]?.subunits ?? {}).length +
        (activeCourseData.units[unitId as UnitId]?.activities ?? []).length,
    );
  }, [activeCourseData, unitId]);

  const handleSubmit = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsLoading(true);

    try {
      const response = await (
        await Request.post('course/createSubunit', {
          subunit: newSubunitData,
          index: unitContentCount,
          unitId: unitId,
        })
      )?.json();

      if (!response || response.error || response.status !== 'success') {
        throw new Error('Failed to add new Subunit');
      }

      const createdSubunit: SubunitData & { id: string } = response.data;

      const normalizedSubunit: SubunitData = {
        name: createdSubunit.name,
        index: createdSubunit.index ?? 0,
        activities: Array.isArray(createdSubunit.activities) ? createdSubunit.activities : [],
        aurora_activities: Array.isArray((createdSubunit as any).aurora_activities)
          ? (createdSubunit as any).aurora_activities
          : [],
      };

      const updatedCourse: CourseData = {
        ...activeCourseData,
        units: {
          ...activeCourseData.units,
          [unitId]: {
            ...activeCourseData.units[unitId],
            subunits: {
              ...activeCourseData.units[unitId].subunits,
              [createdSubunit.id]: normalizedSubunit,
            },
          },
        },
      };

      dispatch(setActiveCourseData(updatedCourse));

      const cookieCourse = UserCourseStorageService.getCourseData();
      if (cookieCourse && cookieCourse.id === activeCourseData.id) {
        const updatedCookieCourse: CourseData = {
          ...cookieCourse,
          units: {
            ...cookieCourse.units,
            [unitId]: {
              ...cookieCourse.units[unitId],
              subunits: {
                ...cookieCourse.units[unitId].subunits,
                [createdSubunit.id]: normalizedSubunit,
              },
            },
          },
        };
        LocalStorageService.set('course-map', updatedCookieCourse);
      }
      toast.success('Subunit added successfully');
      handleClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create subunit');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewSubunitData(DefaultSubunit);
    setConfirmCloseModalOpen(false);
    setIsLoading(false);
    dispatch(setModalVisible(false));
    dispatch(setModalName(''));
  };

  const handleConfirmClose = () => {
    if (newSubunitData.name.trim() !== '') {
      setConfirmCloseModalOpen(true);
    } else {
      handleClose();
    }
  };

  return (
    <>
      <ModalComponent
        isOpen={modalVisible && modalName === 'AddNewSubunit'}
        onRequestClose={handleConfirmClose}
        contentLabel='Create New Subunit'
        contentSubtitle={`Adding subunit ${unitContentCount + 1} to "${activeCourseData.units[unitId]?.name ?? 'unit'}"`}
        className='max-w-[900px] w-full'
        closeOnClickOff={false}
      >
        <div className='relative w-full'>
          <div className='flex flex-col gap-[16px] w-full'>
            <StandardInputText
              id='subunitName'
              label='Subunit Name'
              isRequired={true}
              value={newSubunitData.name}
              onChange={(e) => setNewSubunitData({ ...newSubunitData, name: e.target.value })}
              disabled={isLoading}
            />

            <div className='w-full pt-4 flex flex-col gap-2'>
              <div className='flex flex-row gap-8 py-2'>
                <Button onClick={handleSubmit} disabled={!newSubunitData.name.trim() || isLoading}>
                  <AddIcon className='w-[24px] h-[24px] mr-2' /> Create Subunit
                </Button>
                <Button outline={true} onClick={handleConfirmClose} disabled={isLoading}>
                  <CancelIcon className='w-[20px] h-[20px] mr-2' /> Cancel
                </Button>
              </div>
            </div>
          </div>

          {isLoading && (
            <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
              <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
              <div className='flex flex-col items-center justify-center gap-2 text-sm z-20'>
                <LoadingSpinner />
                <div>Creating subunit...</div>
              </div>
            </div>
          )}
        </div>
      </ModalComponent>

      <ModalComponent
        isOpen={confirmCloseModalOpen}
        onRequestClose={() => setConfirmCloseModalOpen(false)}
        contentLabel='Discard Changes?'
        contentSubtitle='Your changes will be lost'
        className='max-w-[500px] w-full'
        closeOnClickOff={true}
      >
        <div className='relative w-full'>
          <div className='flex flex-col gap-[16px] w-full'>
            <div className='flex flex-col gap-2 bg-surface_gray_200 rounded-lg p-4 border border-border_gray_300'>
              <p className='text-base'>Are you sure you want to discard your changes?</p>
              <p className='text-sm text-text_gray_900'>
                The subunit you&apos;re creating will not be saved.
              </p>
            </div>

            <div className='w-full pt-4 flex flex-col gap-2'>
              <div className='flex flex-row gap-8 py-2'>
                <Button onClick={handleClose} delete>
                  Discard Changes
                </Button>
                <Button outline={true} onClick={() => setConfirmCloseModalOpen(false)}>
                  Continue Editing
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalComponent>
    </>
  );
}

export default SubunitCreatorWizard;
